(() => {
  const moneyText = (value) => value || '';
  const ELECTRIC_LICE_COMB_HANDLE = 'electric-lice-remover';
  const handleize = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/grey/g, 'gray')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const LED_PAIR_NUMBERS = {
    orange: [[1, 49], [46, 45], [47, 3], [48, 50], [2, 44]],
    blue: [[10, 14], [11, 6], [12, 7], [13, 5], [9, 8]],
    green: [[20, 16], [21, 15], [22, 19], [23, 24], [17, 18]],
    pink: [[32, 29], [4, 25], [31, 26], [30, 33], [28, 27]],
    red: [[39, 38], [40, 34], [41, 35], [43, 42], [37, 36]]
  };
  const LED_COLORS = Object.keys(LED_PAIR_NUMBERS);
  const ledFilename = (number) => `furavora-led-halo-${String(number).padStart(3, '0')}.webp`;
  const LED_IMAGE_META = (() => {
    const imageMeta = new Map();
    Object.entries(LED_PAIR_NUMBERS).forEach(([color, pairs]) => {
      pairs.forEach(([daylight, night], pairIndex) => {
        imageMeta.set(ledFilename(daylight), { color, mode: 'daylight', pairIndex });
        imageMeta.set(ledFilename(night), { color, mode: 'night', pairIndex });
      });
    });
    return imageMeta;
  })();

  const thumbFilename = (thumb) => {
    const source = String(thumb?.dataset?.image || thumb?.querySelector('img')?.src || '');
    return source.match(/furavora-led-halo-\d{3}\.webp/i)?.[0]?.toLowerCase() || '';
  };

  const groupTokens = (thumb) => String(thumb?.dataset?.mediaGroups || '')
    .split(/\s+/)
    .map(handleize)
    .filter(Boolean);

  const variantColorTokens = (thumb) => String(thumb?.dataset?.variantColors || '')
    .split('|')
    .map(handleize)
    .filter(Boolean);

  const slotPalette = (value) => {
    const color = handleize(value);
    if (color.includes('pink')) return ['#e8a4bc', '#5f1838'];
    if (color.includes('white') || color.includes('cream')) return ['#f3efe6', '#0d274f'];
    if (color.includes('gray') || color.includes('silver')) return ['#aeb6c1', '#172033'];
    if (color.includes('black')) return ['#252b36', '#ffffff'];
    if (color.includes('navy')) return ['#173d70', '#ffffff'];
    if (color.includes('blue')) return ['#63b6dc', '#0d274f'];
    if (color.includes('red')) return ['#c75b62', '#ffffff'];
    if (color.includes('yellow') || color.includes('gold')) return ['#e5c35f', '#352811'];
    if (color.includes('green')) return ['#78a98a', '#102f20'];
    if (color.includes('purple')) return ['#9d83bd', '#241637'];
    if (color.includes('brown') || color.includes('beige')) return ['#c7a77c', '#372718'];
    return ['#dce7ef', '#0d274f'];
  };

  class FuravoraProduct {
    constructor(root) {
      this.root = root;
      this.variants = this.readVariants();
      this.form = root.querySelector('[data-fv3-product-form]');
      this.mainImage = root.querySelector('.fv6-gallery__main-image');
      this.transitionImage = root.querySelector('[data-fv-light-transition]');
      this.stage = root.querySelector('[data-fv4-stage]');
      this.thumbsContainer = root.querySelector('[data-fv4-thumbs]');
      this.mediaThumbs = [...root.querySelectorAll('[data-fv4-thumb]')];
      this.stagePlaceholder = root.querySelector('[data-fv4-stage-placeholder]');
      this.bundleBadge = root.querySelector('[data-fv-bundle-badge]');
      this.lightingPairs = this.readLightingPairs();
      this.ledThumbs = this.mediaThumbs.filter((thumb) => LED_IMAGE_META.has(thumbFilename(thumb)));
      this.isLedGallery = this.ledThumbs.length === LED_IMAGE_META.size;
      this.ledColor = '';
      this.ledPairIndex = 0;
      this.lightMode = 'daylight';
      this.suppressStageClickUntil = 0;
      this.imageRequestId = 0;
      this.annotateLedThumbs();
      this.initGallery();
      this.initLighting();
      this.initOptions();
      this.initQuantity();
      this.initForm();
      this.syncVariant();
      this.stabilizeVariantInput();
    }

    readVariants() {
      try {
        return JSON.parse(this.root.querySelector('[data-fv3-variants]')?.textContent || '[]');
      } catch (error) {
        return [];
      }
    }

    readLightingPairs() {
      try {
        return JSON.parse(this.root.querySelector('[data-fv-lighting-pairs]')?.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    annotateLedThumbs() {
      this.ledThumbs.forEach((thumb) => {
        const meta = LED_IMAGE_META.get(thumbFilename(thumb));
        if (!meta) return;
        thumb.dataset.fvLedColor = meta.color;
        thumb.dataset.fvLedMode = meta.mode;
        thumb.dataset.fvLedPairIndex = String(meta.pairIndex);
      });
    }

    ledMetaForThumb(thumb) {
      if (!thumb?.dataset?.fvLedColor) return null;
      return {
        color: thumb.dataset.fvLedColor,
        mode: thumb.dataset.fvLedMode === 'night' ? 'night' : 'daylight',
        pairIndex: Number(thumb.dataset.fvLedPairIndex || 0)
      };
    }

    variantFilterCandidates(variant) {
      const primaryValues = [variant?.galleryKey, variant?.color, ...(variant?.options || [])]
        .map(handleize)
        .filter((value) => value && value !== 'all');
      // Keep every normalized option as one exact token. Splitting compound keys
      // would make "light-blue" match "blue" (and "light-brown" match
      // "dark-brown"), which leaks another variant's photos into the gallery.
      return [...new Set(primaryValues)];
    }

    resolveLedColor(variant) {
      const candidates = this.variantFilterCandidates(variant);
      for (const candidate of candidates) {
        if (candidate === 'hot-pink') return 'pink';
        if (LED_COLORS.includes(candidate)) return candidate;
      }
      const activeColor = this.ledMetaForThumb(this.mediaThumbs.find((thumb) => thumb.classList.contains('is-active')))?.color;
      return activeColor || this.ledColor || 'orange';
    }

    selectedOptions() {
      return this.optionGroupsByPosition().map((group) =>
        group.querySelector('[data-fv3-option]:checked')?.value || ''
      );
    }

    customerAllowedVariant(variant) {
      if (this.root?.dataset?.productHandle !== ELECTRIC_LICE_COMB_HANDLE) return true;
      return variant?.options?.includes('Upgrade High Electricity') === true;
    }

    currentVariant() {
      const selected = this.selectedOptions();
      if (!selected.length) return this.variants.find((variant) => this.customerAllowedVariant(variant)) || null;
      return this.variants.find((variant) => (
        this.customerAllowedVariant(variant)
        && variant.options.every((option, index) => option === selected[index])
      )) || null;
    }

    initOptions() {
      this.root.querySelectorAll('[data-fv3-option]').forEach((input) => {
        input.addEventListener('change', () => this.syncVariant({
          position: Number(input.dataset.optionPosition || 1) - 1,
          value: input.value
        }));
      });
    }

    optionGroupsByPosition() {
      return [...this.root.querySelectorAll('[data-fv3-option-group]')]
        .sort((left, right) => Number(left.dataset.optionPosition || 1) - Number(right.dataset.optionPosition || 1));
    }

    closestAvailableVariant(intent = null) {
      const selected = this.selectedOptions();
      const available = this.variants.filter((variant) => variant.available && this.customerAllowedVariant(variant));
      if (!available.length) return null;

      const exact = available.find((variant) => variant.options.every((option, index) => option === selected[index]));
      if (exact && (!intent || exact.options[intent.position] === intent.value)) return exact;

      const candidates = intent
        ? available.filter((variant) => variant.options[intent.position] === intent.value)
        : available;
      if (!candidates.length) return null;

      return candidates.reduce((best, variant) => {
        const score = variant.options.reduce((total, option, index) => {
          if (intent && index === intent.position) return total;
          return total + (option === selected[index] ? 1 : 0);
        }, 0);
        if (!best || score > best.score) return { variant, score };
        return best;
      }, null)?.variant || candidates[0];
    }

    applyVariantOptions(variant) {
      if (!variant) return;
      this.optionGroupsByPosition().forEach((group) => {
        const position = Number(group.dataset.optionPosition || 1) - 1;
        const nextValue = variant.options[position];
        group.querySelectorAll('[data-fv3-option]').forEach((input) => {
          input.checked = input.value === nextValue;
        });
      });
    }

    syncVariant(intent = null) {
      const resolvedVariant = this.closestAvailableVariant(intent);
      if (resolvedVariant) this.applyVariantOptions(resolvedVariant);
      const variant = this.currentVariant();
      this.root.querySelectorAll('[data-fv3-option-group]').forEach((group) => {
        const selected = group.querySelector('[data-fv3-option]:checked');
        const output = group.querySelector('[data-fv3-selected-value]');
        if (output && selected) output.textContent = selected.dataset.displayValue || selected.value;
      });
      this.syncAvailability();
      if (!variant) {
        this.setPurchaseState(false, 'UNAVAILABLE');
        return;
      }

      const price = this.root.querySelector('[data-fv3-price]');
      const compare = this.root.querySelector('[data-fv3-compare-price]');
      const saleBadge = this.root.querySelector('[data-fv3-sale-badge]');
      const promoNote = this.root.querySelector('[data-fv3-promo-note]');
      this.setVariantInput(variant.id, true);
      if (price) price.textContent = moneyText(variant.price);
      if (compare) {
        compare.textContent = moneyText(variant.compareAt);
        compare.hidden = !variant.compareAt;
      }
      if (saleBadge) {
        saleBadge.textContent = variant.salePercent > 0 ? `SAVE ${variant.salePercent}%${variant.savedAmount ? ` • ${variant.savedAmount}` : ''}` : '';
        saleBadge.hidden = !(variant.salePercent > 0);
      }
      if (promoNote) promoNote.hidden = !variant.checkoutPromo;
      this.setPurchaseState(variant.available, variant.available ? 'ADD TO CART' : 'SOLD OUT');
      this.filterGallery(variant);
      this.syncBundleBadge(variant);

      const baseUrl = this.root.dataset.productUrl || window.location.pathname;
      window.history.replaceState({}, '', `${baseUrl}?variant=${variant.id}`);
    }

    setVariantInput(variantId, notify = false) {
      const idInput = this.root.querySelector('[data-fv3-variant-id]');
      if (!idInput || !variantId) return;
      const nextValue = String(variantId);
      const changed = idInput.value !== nextValue;
      idInput.value = nextValue;
      idInput.setAttribute('value', nextValue);
      if (notify && changed) idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    stabilizeVariantInput() {
      // Shopify's accelerated-payment element can finish after this section's script
      // and briefly clear the hidden variant input. Re-assert the selected variant
      // during that short initialization window so enabled providers and the cart form have
      // an authoritative variant ID.
      let checks = 0;
      const keepSelectedVariant = () => {
        checks += 1;
        const variant = this.currentVariant();
        if (variant) this.setVariantInput(variant.id, true);
        if (checks >= 20) window.clearInterval(timer);
      };
      const timer = window.setInterval(keepSelectedVariant, 250);
      window.setTimeout(keepSelectedVariant, 0);
      window.addEventListener('pageshow', keepSelectedVariant, { once: true });
    }

    syncAvailability() {
      this.root.querySelectorAll('[data-fv3-option]').forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        // A value stays actionable when any available Shopify variant contains
        // it. If it conflicts with another current choice, syncVariant keeps the
        // clicked value and moves only the incompatible companion selections.
        const possible = this.variants.some((variant) => (
          variant.available
          && this.customerAllowedVariant(variant)
          && variant.options[position] === input.value
        ));
        input.disabled = !possible && !input.checked;
        input.nextElementSibling?.classList.toggle('is-unavailable', !possible);
      });
    }

    setPurchaseState(available, label) {
      const button = this.root.querySelector('[data-fv3-add]');
      const text = this.root.querySelector('[data-fv3-add-label]');
      const express = this.root.querySelector('[data-fv3-express]');
      if (button) button.disabled = !available;
      if (text) text.textContent = label;
      if (express) express.hidden = !available;
    }

    syncBundleBadge(variant) {
      if (!this.bundleBadge) return;
      const label = String(variant?.bundleLabel || '').trim();
      this.bundleBadge.textContent = label;
      this.bundleBadge.hidden = !label;
    }

    initLighting() {
      if (!this.isLedGallery || !this.stage) return;
      this.root.querySelectorAll('[data-fv-light-mode]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          this.lightMode = button.dataset.fvLightMode === 'night' ? 'night' : 'daylight';
          this.showLightingPair(this.currentVariant(), true);
        });
      });
      this.stage.addEventListener('click', (event) => {
        if (event.target.closest('[data-fv-light-mode]')) return;
        if (!event.target.closest('.fv6-gallery__main-image')) return;
        if (Date.now() < this.suppressStageClickUntil) return;
        this.lightMode = this.lightMode === 'night' ? 'daylight' : 'night';
        this.showLightingPair(this.currentVariant(), true);
      });
      this.stage.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.lightMode = this.lightMode === 'night' ? 'daylight' : 'night';
        this.showLightingPair(this.currentVariant(), true);
      });
    }

    updateLightingButtons() {
      this.root.querySelectorAll('[data-fv-light-mode]').forEach((button) => {
        const active = button.dataset.fvLightMode === this.lightMode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    applyLedVisibility() {
      if (!this.isLedGallery) return [];
      const visible = [];
      this.mediaThumbs.forEach((thumb) => {
        const meta = this.ledMetaForThumb(thumb);
        const isVisible = meta?.color === this.ledColor && meta.mode === this.lightMode;
        thumb.hidden = !isVisible;
        if (isVisible) visible.push(thumb);
      });
      return visible;
    }

    ledThumbFor(color, mode, pairIndex) {
      return this.ledThumbs.find((thumb) => {
        const meta = this.ledMetaForThumb(thumb);
        return meta?.color === color && meta.mode === mode && meta.pairIndex === pairIndex;
      });
    }

    showLightingPair(variant, animate = true) {
      if (!this.isLedGallery) return;
      this.ledColor = this.resolveLedColor(variant);
      const target = this.ledThumbFor(this.ledColor, this.lightMode, this.ledPairIndex)
        || this.ledThumbFor(this.ledColor, this.lightMode, 0);
      if (!target) return;
      this.showMedia(target, { animateLighting: animate });
    }

    initGallery() {
      this.mediaThumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb));
        thumb.addEventListener('pointerenter', () => this.preloadMedia(thumb), { once: true });
        thumb.addEventListener('focus', () => this.preloadMedia(thumb), { once: true });
      });

      // Warm the first few gallery targets quietly. No visible loading animation.
      const warm = () => this.mediaThumbs.slice(0, 4).forEach((thumb) => this.preloadMedia(thumb));
      if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 900 });
      else window.setTimeout(warm, 250);

      if (!this.stage) return;
      let touchStartX = null;
      this.stage.addEventListener('touchstart', (event) => {
        touchStartX = event.touches[0]?.clientX ?? null;
      }, { passive: true });
      this.stage.addEventListener('touchend', (event) => {
        if (touchStartX === null) return;
        const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX;
        const distance = touchEndX - touchStartX;
        touchStartX = null;
        if (Math.abs(distance) < 45) return;
        // Mobile browsers may emit a synthetic click after touchend. Suppress it
        // so one swipe advances exactly once instead of also toggling LED mode.
        this.suppressStageClickUntil = Date.now() + 700;
        this.showAdjacentMedia(distance < 0 ? 1 : -1);
      }, { passive: true });
      this.stage.addEventListener('touchcancel', () => {
        touchStartX = null;
      }, { passive: true });
      this.stage.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.showAdjacentMedia(event.key === 'ArrowRight' ? 1 : -1);
      });
    }

    showAdjacentMedia(direction) {
      const visible = this.mediaThumbs.filter((thumb) => !thumb.hidden);
      if (visible.length < 2) return;
      const current = visible.findIndex((thumb) => thumb.classList.contains('is-active'));
      const next = current < 0
        ? 0
        : (current + direction + visible.length) % visible.length;
      this.showMedia(visible[next]);
    }

    filterGallery(variant) {
      this.clearGeneratedSlots();
      if (this.isLedGallery) {
        this.ledColor = this.resolveLedColor(variant);
        const visible = this.applyLedVisibility();
        const target = this.ledThumbFor(this.ledColor, this.lightMode, this.ledPairIndex)
          || visible[0];
        this.showMedia(target);
        return;
      }

      const galleryKey = handleize(variant?.galleryKey);
      const candidates = this.variantFilterCandidates(variant);
      const allImages = this.mediaThumbs.filter((thumb) => groupTokens(thumb).includes('all'));
      const exactGroupImages = this.mediaThumbs.filter((thumb) => {
        const groups = groupTokens(thumb);
        return groups.some((group) => group !== 'all' && candidates.includes(group));
      });
      let visible;

      if (galleryKey === 'all' && allImages.length) {
        visible = allImages;
      } else if (exactGroupImages.length) {
        visible = [...new Set([...allImages, ...exactGroupImages])];
      } else {
        // A product may use native Shopify media instead of the local manifest.
        // Only use exact data attributes or the variant's featured media here;
        // never borrow images from a similarly named custom color/style.
        const shopifyThumbs = this.mediaThumbs.filter((thumb) =>
          !String(thumb.dataset.mediaId || '').startsWith('furavora-local-')
        );
        const exactShopifyImages = shopifyThumbs.filter((thumb) =>
          variantColorTokens(thumb).some((color) => candidates.includes(color))
        );
        const preferred = shopifyThumbs.find((thumb) =>
          String(thumb.dataset.mediaId) === String(variant?.featuredMediaId)
        );

        if (exactShopifyImages.length) visible = exactShopifyImages;
        else if (preferred) visible = [preferred];
        else if (allImages.length) visible = allImages;
        else if (!candidates.length) visible = this.mediaThumbs;
        else {
          const current = this.mediaThumbs.find((thumb) => thumb.classList.contains('is-active'));
          visible = current ? [current] : this.mediaThumbs.slice(0, 1);
        }
      }

      const visibleSet = new Set(visible);
      this.mediaThumbs.forEach((thumb) => {
        thumb.hidden = !visibleSet.has(thumb);
      });

      const preferred = visible.find((thumb) => String(thumb.dataset.mediaId) === String(variant?.featuredMediaId));
      const current = this.mediaThumbs.find((thumb) => thumb.classList.contains('is-active'));
      const active = visible.includes(current)
        ? current
        : null;
      this.showMedia(preferred || active || visible[0]);
    }

    preloadMedia(thumb) {
      const url = thumb?.dataset?.image;
      if (!url || thumb.dataset.fvPreloaded === 'true') return;
      thumb.dataset.fvPreloaded = 'true';
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
    }

    showMedia(thumb, options = {}) {
      if (!thumb) return;
      const ledMeta = this.ledMetaForThumb(thumb);
      if (ledMeta) {
        this.ledColor = ledMeta.color;
        this.lightMode = ledMeta.mode;
        this.ledPairIndex = ledMeta.pairIndex;
        this.applyLedVisibility();
        this.updateLightingButtons();
      }

      const requestId = ++this.imageRequestId;
      [...this.mediaThumbs, ...this.root.querySelectorAll('[data-fv4-generated-slot]')].forEach((item) => {
        const isActive = item === thumb;
        item.classList.toggle('is-active', isActive);
        if (isActive) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });

      if (thumb.hasAttribute('data-fv4-generated-slot')) {
        if (this.mainImage) this.mainImage.hidden = true;
        if (this.stagePlaceholder) {
          const [background, ink] = slotPalette(thumb.dataset.slotColor);
          this.stagePlaceholder.hidden = false;
          this.stagePlaceholder.style.setProperty('--fv6-slot-color', background);
          this.stagePlaceholder.style.setProperty('--fv6-slot-ink', ink);
          const colorLabel = this.stagePlaceholder.querySelector('[data-fv4-slot-color]');
          const angleLabel = this.stagePlaceholder.querySelector('[data-fv4-slot-label]');
          if (colorLabel) colorLabel.textContent = String(thumb.dataset.slotColor || 'Product').toUpperCase();
          if (angleLabel) angleLabel.textContent = `ANGLE ${thumb.dataset.slotAngle}`;
        }
        return;
      }

      if (!this.mainImage) return;
      const nextUrl = thumb.dataset.image;
      const baseAlt = String(thumb.dataset.alt || this.mainImage.alt || '').split(' — ')[0];
      const nextAlt = ledMeta
        ? `${baseAlt} — ${ledMeta.color.replace(/-/g, ' ')} ${ledMeta.mode === 'night' ? 'night light' : 'daylight'} preview`
        : (thumb.dataset.alt || this.mainImage.alt);
      const clearTransition = () => {
        if (!this.transitionImage) return;
        this.transitionImage.classList.remove('is-visible');
        this.transitionImage.hidden = true;
      };
      const applyImage = () => {
        if (requestId !== this.imageRequestId) return;
        clearTransition();
        this.mainImage.hidden = false;
        if (this.stagePlaceholder) this.stagePlaceholder.hidden = true;
        this.mainImage.removeAttribute('srcset');
        this.mainImage.removeAttribute('sizes');
        this.mainImage.classList.remove('is-entering');
        this.mainImage.src = nextUrl;
        this.mainImage.alt = nextAlt;
      };

      const animateLighting = Boolean(options.animateLighting && ledMeta && this.transitionImage);

      // Keep the current image visible until the requested image is ready, then swap instantly.
      // This avoids the "loading slide" feeling on product galleries.
      if (!nextUrl || this.mainImage.src === new URL(nextUrl, window.location.href).href) {
        applyImage();
        return;
      }
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        if (requestId !== this.imageRequestId) return;
        if (!animateLighting) {
          applyImage();
          return;
        }

        this.transitionImage.src = nextUrl;
        this.transitionImage.alt = nextAlt;
        this.transitionImage.hidden = false;
        window.requestAnimationFrame(() => {
          if (requestId === this.imageRequestId) this.transitionImage.classList.add('is-visible');
        });
        window.setTimeout(() => {
          if (requestId !== this.imageRequestId) return;
          this.mainImage.hidden = false;
          if (this.stagePlaceholder) this.stagePlaceholder.hidden = true;
          this.mainImage.removeAttribute('srcset');
          this.mainImage.removeAttribute('sizes');
          this.mainImage.classList.remove('is-entering');
          this.mainImage.src = nextUrl;
          this.mainImage.alt = nextAlt;
          this.transitionImage.classList.remove('is-visible');
          window.setTimeout(() => {
            if (requestId === this.imageRequestId) this.transitionImage.hidden = true;
          }, 280);
        }, 320);
      };
      image.onerror = applyImage;
      image.src = nextUrl;
    }

    clearGeneratedSlots() {
      this.root.querySelectorAll('[data-fv4-generated-slot]').forEach((slot) => slot.remove());
    }

    createAngleSlots(colorLabel, count, existingCount) {
      if (!this.thumbsContainer || count <= 0) return [];
      const [background, ink] = slotPalette(colorLabel);
      const slots = [];
      for (let index = 0; index < count; index += 1) {
        const angle = existingCount + index + 1;
        const button = document.createElement('button');
        const colorText = document.createElement('span');
        const angleText = document.createElement('strong');
        button.type = 'button';
        button.className = 'fv6-gallery__thumb fv6-gallery__thumb--slot';
        button.dataset.fv4GeneratedSlot = '';
        button.dataset.slotColor = colorLabel;
        button.dataset.slotAngle = angle;
        button.setAttribute('aria-label', `${colorLabel} angle ${angle} image slot`);
        button.style.setProperty('--fv6-slot-color', background);
        button.style.setProperty('--fv6-slot-ink', ink);
        colorText.textContent = String(colorLabel).toUpperCase();
        angleText.textContent = `ANGLE ${angle}`;
        button.append(colorText, angleText);
        button.addEventListener('click', () => this.showMedia(button));
        this.thumbsContainer.append(button);
        slots.push(button);
      }
      return slots;
    }

    initQuantity() {
      const input = this.root.querySelector('[data-fv3-quantity] input');
      this.root.querySelector('[data-fv3-quantity-minus]')?.addEventListener('click', () => {
        if (input) input.value = Math.max(1, Number(input.value || 1) - 1);
      });
      this.root.querySelector('[data-fv3-quantity-plus]')?.addEventListener('click', () => {
        if (input) input.value = Math.max(1, Number(input.value || 1) + 1);
      });
    }

    initForm() {
      if (!this.form) return;
      this.form.addEventListener('submit', async (event) => {
        if (!event.submitter?.matches('[data-fv3-add]')) return;
        event.preventDefault();
        const button = this.root.querySelector('[data-fv3-add]');
        const label = this.root.querySelector('[data-fv3-add-label]');
        const status = this.root.querySelector('[data-fv3-status]');
        const cartLink = this.root.querySelector('[data-fv3-cart-link]');
        if (button) {
          button.disabled = true;
          button.classList.remove('is-added');
        }
        if (label) label.textContent = 'ADDING…';
        if (status) status.textContent = '';

        let added = false;
        try {
          const variant = this.currentVariant();
          if (!variant?.id || !variant.available) throw new Error('Please choose an available option.');
          this.setVariantInput(variant.id, true);
          const formData = new FormData(this.form);
          // Always set the authoritative selected variant immediately before the
          // request. This remains reliable even if an accelerated-payment script
          // has changed the form's hidden input during its own initialization.
          formData.set('id', String(variant.id));
          const response = await fetch(this.form.action, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            body: formData
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'Could not add this item.');
          added = true;
          if (button) button.classList.add('is-added');
          if (label) label.textContent = 'ADDED TO CART ✓';
          if (status) status.textContent = 'Added successfully. Your cart is ready.';
          if (cartLink) cartLink.hidden = false;
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } catch (error) {
          if (status) status.textContent = error.message || 'Could not add this item. Please try again.';
        } finally {
          const variant = this.currentVariant();
          if (button) button.disabled = !variant?.available;
          if (!added && label) label.textContent = variant?.available ? 'ADD TO CART' : 'SOLD OUT';
          if (added) {
            window.setTimeout(() => {
              button?.classList.remove('is-added');
              const current = this.currentVariant();
              if (label) label.textContent = current?.available ? 'ADD TO CART' : 'SOLD OUT';
            }, 1800);
          }
        }
      });
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-fv3-product]').forEach((root) => {
      if (root.dataset.fv4Ready === 'true') return;
      root.dataset.fv4Ready = 'true';
      new FuravoraProduct(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
